// Unified Authentication Service for all user types
import { supabase } from '../supabase/supabaseClient';

class AuthService {
  constructor() {
    this.currentUser = null;
    this.userRole = null;
    this.authListeners = [];
  }

  // Initialize auth state from localStorage
  async init() {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        this.currentUser = userData;
        this.userRole = userData.user_type || userData.role;
        return userData;
      } catch (error) {
        console.error('Error parsing stored user:', error);
        this.logout();
      }
    }
    return null;
  }

  // Unified login for all user types
  async login(identifier, password) {
    try {
      // For patients: identifier is access code
      // For clinicians/admins: identifier is username
      let query;
      
      if (identifier.length === 6 && /^\d+$/.test(identifier)) {
        // Access code login (patient)
        query = supabase
          .from('patients')
          .select('*')
          .eq('access_code', identifier)
          .single();
      } else {
        // Username/password login (clinician/admin)
        const { data: userData } = await supabase
          .from('user')
          .select('*')
          .eq('name', identifier)
          .single();

        if (!userData) {
          throw new Error('User not found');
        }

        // Verify password (in production, use proper hashing)
        const { data: verified } = await supabase
          .rpc('verify_user_login', {
            user_name: identifier,
            user_password: password
          });

        if (!verified || verified.length === 0) {
          throw new Error('Invalid credentials');
        }

        query = { data: verified[0] };
      }

      const { data, error } = await query;
      
      if (error) throw error;

      // Determine user role and dashboard
      const userRole = data.user_type || data.role || 'patient';
      const dashboardRoute = this.getDashboardRoute(userRole);

      // Store user data
      const userData = {
        id: data.id,
        name: data.name || data.firstName,
        role: userRole,
        access_code: data.access_code,
        clinician_id: data.clinician_id,
        isAuthenticated: true,
        dashboard: dashboardRoute
      };

      this.currentUser = userData;
      this.userRole = userRole;
      
      // Persist to localStorage
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Update last login
      await supabase
        .from('user')
        .update({ last_login: new Date().toISOString() })
        .eq('id', data.id);

      // Notify listeners
      this.notifyAuthChange(userData);

      return {
        success: true,
        user: userData,
        redirect: dashboardRoute
      };

    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Logout
  logout() {
    this.currentUser = null;
    this.userRole = null;
    localStorage.removeItem('user');
    localStorage.removeItem('hume_voice_active');
    this.notifyAuthChange(null);
    window.location.href = '/';
  }

  // Get dashboard route based on role
  getDashboardRoute(role) {
    switch (role?.toLowerCase()) {
      case 'superadmin':
      case 'admin':
        return '/dashboard/superadmin';
      case 'clinician':
      case 'therapist':
        return '/dashboard/clinician';
      case 'patient':
      default:
        return '/dashboard/patient';
    }
  }

  // Check if user has permission for a route
  hasPermission(requiredRole) {
    const roleHierarchy = {
      'superadmin': 3,
      'admin': 3,
      'clinician': 2,
      'therapist': 2,
      'patient': 1
    };

    const userLevel = roleHierarchy[this.userRole?.toLowerCase()] || 0;
    const requiredLevel = roleHierarchy[requiredRole?.toLowerCase()] || 0;

    return userLevel >= requiredLevel;
  }

  // Get current user
  getCurrentUser() {
    return this.currentUser;
  }

  // Get user role
  getUserRole() {
    return this.userRole;
  }

  // Check if authenticated
  isAuthenticated() {
    return !!this.currentUser;
  }

  // Subscribe to auth changes
  onAuthChange(callback) {
    this.authListeners.push(callback);
    return () => {
      this.authListeners = this.authListeners.filter(cb => cb !== callback);
    };
  }

  // Notify auth listeners
  notifyAuthChange(user) {
    this.authListeners.forEach(callback => callback(user));
  }

  // Get patients for clinician
  async getClinicianPatients(clinicianId) {
    const { data, error } = await supabase
      .from('patients')
      .select('*')
      .eq('clinician_id', clinicianId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  }

  // Get all users for superadmin
  async getAllUsers() {
    if (!this.hasPermission('superadmin')) {
      throw new Error('Insufficient permissions');
    }

    const { data: clinicians } = await supabase
      .from('user')
      .select('*')
      .eq('user_type', 'clinician');

    const { data: patients } = await supabase
      .from('patients')
      .select('*');

    return {
      clinicians: clinicians || [],
      patients: patients || []
    };
  }

  // Create new patient (for clinicians)
  async createPatient(patientData) {
    if (!this.hasPermission('clinician')) {
      throw new Error('Insufficient permissions');
    }

    // Generate 6-digit access code
    const accessCode = Math.floor(100000 + Math.random() * 900000).toString();

    const { data, error } = await supabase
      .from('patients')
      .insert({
        ...patientData,
        access_code: accessCode,
        clinician_id: this.currentUser.id,
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;